import { defineStore } from 'pinia';
import { useIdentityStore } from '@/store/IdentityStore';
import * as UnitConversion from '@/services/UnitConversionService';

export const useSettingsStore = defineStore('settings', {
  state () {
    return {
      isFlush: false,
      lengthIndex: 0,
      pressureIndex: 0,
      flowIndex: 0,
      emitterFlowIndex: 0,
      velocityIndex: 0,
      slopeIndex: 0,
      timeIndex: 1,
      diameterIndex: 0,
      graphSettings: {
        pressureYAxisDivision: 7,
        flowYAxisDivision: 7,
        topChartSpacing: 35,
        raiseYAxisOrigin: true,
        sprinklerPressure: {
          min: UnitConversion.ConvertPSItoPAS(10),
          max: UnitConversion.ConvertPSItoPAS(15),
          markers: true
        },
        sprinklerFlow: {
          min: UnitConversion.ConvertGPMtoLPM(0.1),
          max: UnitConversion.ConvertGPMtoLPM(0.2),
          markers: false
        },
        pipePressure: {
          min: UnitConversion.ConvertPSItoPAS(8),
          max: UnitConversion.ConvertPSItoPAS(50),
          markers: true
        },
        velocity: {
          min: 20,
          max: 201,
          markers: false
        },
        graphVisibility: {
          sprinklerPressure: false,
          sprinklerFlow: false,
          pipePressure: false,
          velocity: false
        },
        settingExpanded: {
          sprinklerPressure: false,
          sprinklerFlow: false,
          pipePressure: false,
          velocity: false
        }
      },
      // css class definition located in PipeEditor
      isMonochrome: false
    };
  },
  getters: {
    IsFlush () {
      return this.isFlush;
    },
    getIsMonochrome () {
      return this.isMonochrome;
    },
    getLengthUnit () {
      return this.lengthOptions[this.lengthIndex];
    },
    getPressureUnit () {
      return this.pressureOptions[this.pressureIndex];
    },
    getFlowUnit () {
      return this.flowOptions[this.flowIndex];
    },
    getEmitterFlowUnit () {
      return this.emitterFlowOptions[this.emitterFlowIndex];
    },
    getVelocityUnit () {
      return this.velocityOptions[this.velocityIndex];
    },
    getSlopeUnit () {
      return this.slopeOptions[this.slopeIndex];
    },
    getTimeUnit () {
      return this.timeOptions[this.timeIndex];
    },
    getDiameterUnit () {
      return this.diameterOptions[this.diameterIndex];
    },
    lengthOptions () {
      return ['ft', 'm'];
    },
    pressureOptions () {
      return ['psi', 'm', 'kPa', 'bar', 'kg/cm2', 'atm', 'ft'];
    },
    flowOptions () {
      return ['gpm', 'lpm', 'gph', 'lph', 'm3/h', 'm3/m', 'm3/s'];
    },
    emitterFlowOptions () {
      return ['gpm', 'lpm', 'gph', 'lph', 'm3/h'];
    },
    velocityOptions () {
      return ['ft/s', 'm/s'];
    },
    slopeOptions () {
      return ['%', 'ft', 'm'];
    },
    timeOptions () {
      return ['ms', 's', 'Mins', 'Hours', 'Days'];
    },
    diameterOptions () {
      return ['in', 'mm', 'cm', 'm', 'ft'];
    },
    units () {
      return (
        this.lengthIndex +
        ':' +
        this.pressureIndex +
        ':' +
        this.flowIndex +
        ':' +
        this.emitterFlowIndex +
        ':' +
        this.velocityIndex +
        ':' +
        this.slopeIndex +
        ':' +
        this.timeIndex +
        ':' +
        this.diameterIndex
      );
    },
    getPressureYAxisDivision () {
      return this.graphSettings.pressureYAxisDivision;
    },
    getFlowYAxisDivision () {
      return this.graphSettings.flowYAxisDivision;
    },
    getRaiseYAxisOrigin () {
      return this.graphSettings.raiseYAxisOrigin;
    },
    getSprinklerPressure () {
      return this.graphSettings.sprinklerPressure;
    },
    getSprinklerFlow () {
      return this.graphSettings.sprinklerFlow;
    },
    getSprinklerPressureVisibility () {
      return this.graphSettings.graphVisibility.sprinklerPressure;
    },
    getSprinklerFlowVisibility () {
      return this.graphSettings.graphVisibility.sprinklerFlow;
    },
    getGraphTopChartSpacing () {
      return this.graphSettings.topChartSpacing;
    }
  },
  actions: {
    setIsMonochrome (value) {
      this.isMonochrome = value;
    },
    setRaiseYAxisOrigin (value) {
      this.graphSettings.raiseYAxisOrigin = value;
    },
    getGraphSettingsVisibilityOfType (type) {
      return this.graphSettings.graphVisibility[type];
    },
    getMinSprinklerPressure () {
      return this.graphSettings.sprinklerPressure.min;
    },
    getMaxSprinklerPressure () {
      return this.graphSettings.sprinklerPressure.max;
    },
    getMinSprinklerFlow () {
      return this.graphSettings.sprinklerFlow.min;
    },
    getMaxSprinklerFlow () {
      return this.graphSettings.sprinklerFlow.max;
    },
    getMinPipePressure () {
      return this.graphSettings.pipePressure.min;
    },
    getMaxPipePressure () {
      return this.graphSettings.pipePressure.max;
    },
    getMinVelocity () {
      return this.graphSettings.velocity.min;
    },
    getMaxVelocity () {
      return this.graphSettings.velocity.max;
    },
    getGraphSettingExpanded (type) {
      return this.graphSettings.settingExpanded[type];
    },
    getGraphSettingMarkersOfType (type) {
      return this.graphSettings[type].markers;
    },
    setGraphSettingMarkersOfType (type, value) {
      this.graphSettings[type].markers = value;
    },
    setMinSprinklerPressure (value) {
      this.graphSettings.sprinklerPressure.min = value;
    },
    setMaxSprinklerPressure (value) {
      this.graphSettings.sprinklerPressure.max = value;
    },
    setMinSprinklerFlow (value) {
      this.graphSettings.sprinklerFlow.min = value;
    },
    setMaxSprinklerFlow (value) {
      this.graphSettings.sprinklerFlow.max = value;
    },
    setMinPipePressure (value) {
      this.graphSettings.pipePressure.min = value;
    },
    setMaxPipePressure (value) {
      this.graphSettings.pipePressure.max = value;
    },
    setMinVelocity (value) {
      this.graphSettings.velocity.min = value;
    },
    setMaxVelocity (value) {
      this.graphSettings.velocity.max = value;
    },
    toggleSprinklerPressureVisibility () {
      this.graphSettings.graphVisibility.sprinklerPressure = !this.graphSettings.graphVisibility.sprinklerPressure;
    },
    toggleSprinklerFlowVisibility () {
      this.graphSettings.graphVisibility.sprinklerFlow = !this.graphSettings.graphVisibility.sprinklerFlow;
    },
    togglePipePressureVisibility () {
      this.graphSettings.graphVisibility.pipePressure = !this.graphSettings.graphVisibility.pipePressure;
    },
    toggleVelocityVisibility () {
      this.graphSettings.graphVisibility.velocity = !this.graphSettings.graphVisibility.velocity;
    },
    toggleGraphSettingExpansionForType (type) {
      this.graphSettings.settingExpanded[type] = !this.graphSettings.settingExpanded[type];
    },
    setLengthUnit (value) {
      // needs this if slope is in m or ft. Updates slope units whem length is changed
      if (this.slopeIndex != 0) {
        this.slopeIndex = this.slopeOptions.indexOf(value);
      }
      this.lengthIndex = this.lengthOptions.indexOf(value);
      useIdentityStore().setUserProperty({ Units: this.units });
    },
    setPressureUnit (value) {
      this.pressureIndex = this.pressureOptions.indexOf(value);
      useIdentityStore().setUserProperty({ Units: this.units });
    },
    setFlowUnit (value) {
      this.flowIndex = this.flowOptions.indexOf(value);
      useIdentityStore().setUserProperty({ Units: this.units });
    },
    setEmitterFlowUnit (value) {
      this.emitterFlowIndex = this.emitterFlowOptions.indexOf(value);
      useIdentityStore().setUserProperty({ Units: this.units });
    },
    setVelocityUnit (value) {
      this.velocityIndex = this.velocityOptions.indexOf(value);
      useIdentityStore().setUserProperty({ Units: this.units });
    },
    setSlopeUnit (value) {
      this.slopeIndex = this.slopeOptions.indexOf(value);
      useIdentityStore().setUserProperty({ Units: this.units });
    },
    setTimeUnit (value) {
      this.timeIndex = this.timeOptions.indexOf(value);
      useIdentityStore().setUserProperty({ Units: this.units });
    },
    setDiameterUnit (value) {
      this.diameterIndex = this.diameterOptions.indexOf(value);
      useIdentityStore().setUserProperty({ Units: this.units });
    },
    setFlushShow (value) {
      this.isFlush = value;
      useIdentityStore().setUserProperty({ IsFlush: JSON.stringify(value) });
    },
    restoreUnits () {
      const identityStore = useIdentityStore();
      var storedUnits = identityStore.User.properties.rotator_Units;
      var storedFlush = identityStore.User.properties.rotator_IsFlush;
      if (storedUnits == undefined) {
        identityStore.setUserProperty({ Units: this.units });
        return;
      }
      if (storedFlush == undefined) {
        identityStore.setUserProperty({ IsFlush: 'false' });
        this.isFlush = false;
      }
      else
      {
        this.isFlush = JSON.parse(storedFlush);
      }
      var units = storedUnits.replace(/"/g,'').split(':');
      this.lengthIndex = parseInt(units[0]);
      this.pressureIndex = parseInt(units[1]);
      this.flowIndex = parseInt(units[2]);
      this.emitterFlowIndex = parseInt(units[3]);
      this.velocityIndex = parseInt(units[4]);
      this.slopeIndex = parseInt(units[5]);
      this.timeIndex = parseInt(units[6]);
      this.diameterIndex = parseInt(units[7]);
      this.rateIndex = parseInt(units[8]);
    },
    resetGraphSettingsExpansion () {
      this.graphSettings.settingExpanded = {
        sprinklerPressure: false,
        sprinklerFlow: false,
        pipePressure: false,
        velocity: false
      };
    },
    prettyChartStep (step) {
      if (0.01 < step && step <= 0.1) { step = Math.ceil(step * 100) / 100; }
      else if (0.1 < step && step <= 1) { step = Math.ceil(step * 10) / 10; }
      else if (1 < step && step <= 10) { step = Math.ceil(step); }
      else if (10 < step && step <= 100) { step = Math.ceil(step / 10) * 10; }
      else if (100 < step && step <= 1000) { step = Math.ceil(step / 100) * 100; }
      return step;
    },
    prettyChartMin (min) {
      if (0.01 < min && min <= 0.1) { min = Math.floor(min * 1000) / 1000; }
      else if (0.1 < min && min <= 1) { min = Math.floor(min * 100) / 100; }
      else if (1 < min && min <= 10) { min = Math.floor(min * 10) / 10; }
      else if (10 < min && min <= 100) { min = Math.floor(min); }
      else if (100 < min && min <= 1000) { min = Math.floor(min / 10) * 10; }
      else if (1000 < min && min <= 10000) { min = Math.floor(min / 100) * 100; }
      return min;
    },
    chartStep (filteredMax, filteredMin, divisions) {
      return this.getRaiseYAxisOrigin ? (filteredMax - this.prettyChartMin(filteredMin)) / divisions : filteredMax / divisions;
    },
    filterLength (length) {
      return (UnitConversion.FilterLength(length, useSettingsStore().getLengthUnit));
    },
    filterPressure (pressure) {
      return (UnitConversion.FilterPressure(pressure, useSettingsStore().getPressureUnit));
    },
    filterDiameter (diameter) {
      return (UnitConversion.FilterDiameter(diameter, useSettingsStore().getDiameterUnit));
    },
    filterEmitterFlow (flow) {
      return (UnitConversion.FilterEmitterFlow(flow, useSettingsStore().getEmitterFlowUnit));
    },
    unfilterPressure (pressure) {
      return (UnitConversion.UnfilterPressure(pressure, useSettingsStore().getPressureUnit));
    },
    unfilterEmitterFlow (flow) {
      return (UnitConversion.UnfilterEmitterFlow(flow, useSettingsStore().getEmitterFlowUnit));
    }
  },
});
